<template>
    <div class="flex flex-col h-full">
        <!-- Chat Header -->
        <div class="flex-none flex items-center justify-between px-4 h-14 border-b border-slate-200 bg-white">
            <div class="flex items-center gap-2.5 min-w-0">
                <div class="flex-none w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                    <UIcon name="i-ion-sparkles" class="w-4 h-4 text-white" />
                </div>
                <div class="min-w-0">
                    <div class="text-sm font-semibold text-slate-900 leading-tight truncate">{{ currentChatMeta?.icpName || 'Coldreach AI' }}</div>
                    <div class="text-[11px] text-slate-500 leading-tight truncate">{{ chatHeaderSubtitle }}</div>
                </div>
            </div>
            <div class="flex items-center gap-1">
                <UDropdownMenu :items="chatDropdownItems" :content="{ side: 'bottom', align: 'end' }" :ui="{ label: 'text-[11px] text-slate-400 font-medium uppercase tracking-wider' }">
                    <UButton size="xs" color="neutral" variant="ghost"
                        icon="i-ion-chatbubbles" @click="() => refreshAllChats()" />
                </UDropdownMenu>
                <UButton size="xs" color="neutral" variant="ghost"
                    icon="i-ion-add" @click="startNewChat" />
                <UButton size="xs" color="neutral" variant="ghost"
                    icon="i-heroicons-arrows-pointing-out" @click="globalChat.onChatMaximize()" />
                <UButton size="xs" color="neutral" variant="ghost"
                    icon="i-ion-close" aria-label="Close Coldreach AI" @click="globalChat.closeChat()" />
            </div>
        </div>
        <!-- Chat Content -->
        <div class="flex-1 min-h-0">
            <CampaignEditChat :key="`${globalChat.chatId.value}:${globalChat.chatContext.value.icpId ?? 'none'}`" :context="globalChat.chatContext.value"
                :initial-chat-id="globalChat.chatId.value"
                :initial-prompt="globalChat.chatContext.value.initialPrompt"
                :prefill-prompt="globalChat.chatContext.value.prefillPrompt"
                :last-used-by-other-user-label="lastUsedByOtherUserLabel"
                :is-draft-chat="globalChat.isDraftChat.value"
                @campaign-created="globalChat.onChatCampaignCreated"
                @campaign-launched="globalChat.onChatCampaignLaunched" />
        </div>
    </div>
</template>

<script setup lang="ts">
const globalChat = useGlobalChat();

// Fetch all chats for the conversation dropdown
const { data: allChats, refresh: refreshAllChats } = useAsyncData(
    'global-chat-list',
    () => $fetch('/api/chats'),
    { server: false }
);

// Refresh chat list when switching conversations so header metadata stays current
watch(() => globalChat.chatId.value, () => refreshAllChats());

// Current chat metadata for header display
const currentChatMeta = computed(() => {
    if (!globalChat.chatId.value || !allChats.value) return null;
    return allChats.value.find(c => c.id === globalChat.chatId.value) ?? null;
});

const chatHeaderSubtitle = computed(() => {
    if (!currentChatMeta.value) return 'Campaign assistant';
    const parts = [`Owner: ${currentChatMeta.value.ownerLabel ?? currentChatMeta.value.ownerName ?? 'Unknown'}`];
    const lastUser = currentChatMeta.value.lastMessageUserLabel ?? currentChatMeta.value.lastMessageUserName;
    if (lastUser) parts.push(`Last used by: ${lastUser}`);
    return parts.join(' · ');
});

const lastUsedByOtherUserLabel = computed(() => {
    if (!currentChatMeta.value || currentChatMeta.value.wasLastUsedByCurrentUser !== false) return undefined;
    return currentChatMeta.value.lastMessageUserLabel ?? currentChatMeta.value.lastMessageUserName ?? undefined;
});

function chatLabel(c: { title?: string | null; lastMessageBody?: string | null; icpName?: string | null; ownerLabel?: string | null }, showCampaign = false) {
    const base = c.title || c.lastMessageBody?.slice(0, 60) || 'Empty conversation';
    const parts = [base];
    if (showCampaign && c.icpName) parts.push(c.icpName);
    if (c.ownerLabel) parts.push(`Owner: ${c.ownerLabel}`);
    return parts.join(' — ');
}

function toChatItem(c: { id: string; title?: string | null; lastMessageBody?: string | null; icpId?: number | null; icpName?: string | null; ownerLabel?: string | null }, showCampaign = false) {
    return {
        label: chatLabel(c, showCampaign),
        icon: c.icpName ? 'i-ion-megaphone' : 'i-ion-chatbubble',
        onSelect: () => globalChat.switchChat(c.id, { page: 'default', icpId: c.icpId ?? undefined, initialPrompt: undefined, prefillPrompt: undefined, greeting: undefined, intent: undefined }),
    };
}

function startNewChat() {
    const icpId = globalChat.chatContext.value.icpId;
    globalChat.startNewChat(icpId ? { page: 'campaign-editor', icpId } : undefined);
}

const chatDropdownItems = computed(() => {
    const chats = allChats.value ?? [];
    const withMessages = chats.filter(c => c.messagesCount > 0);
    const currentIcpId = globalChat.chatContext.value.icpId;
    const items: Array<Array<{ label: string; icon: string; onSelect: () => void; type?: string }>> = [];

    if (currentIcpId) {
        const thisCampaign = withMessages.filter(c => c.icpId === currentIcpId);
        const others = withMessages.filter(c => c.icpId !== currentIcpId);

        if (thisCampaign.length > 0) {
            items.push([
                { label: 'This Campaign', icon: '', onSelect: () => {}, type: 'label' },
                ...thisCampaign.slice(0, 10).map(c => toChatItem(c)),
            ]);
        }
        if (others.length > 0) {
            items.push([
                { label: 'Other Campaigns', icon: '', onSelect: () => {}, type: 'label' },
                ...others.slice(0, 10).map(c => toChatItem(c, true)),
            ]);
        }
    } else if (withMessages.length > 0) {
        items.push(withMessages.slice(0, 15).map(c => toChatItem(c, true)));
    }

    items.push([{
        label: 'New conversation',
        icon: 'i-ion-add',
        onSelect: startNewChat,
    }]);

    return items;
});
</script>
